import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

export const TermsOfService = ({ open, onOpenChange }) => {
  const lastUpdated = 'February 14, 2026';
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="terms-of-service-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">Terms of Service</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] pr-4">
          <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
            <p className="text-muted-foreground">Last Updated: {lastUpdated}</p>
            
            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction & Acceptance of Terms</h2>
              <p>
                Welcome to Cash Forth ("we," "our," "us," or the "Company"). Cash Forth is a simple 
                web-based spreadsheet tool designed for personal cash flow forecasting. The Service 
                allows you to enter financial data, create projections, and visualize your future 
                account balances.
              </p>
              <p>
                By creating an account, accessing, or using Cash Forth (the "Service"), you agree to 
                be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
                please do not use the Service.
              </p>
              <p>
                Please also review our{' '}
                <button 
                  onClick={() => onOpenChange(false)}
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </button>
                , which describes how we collect, use, and protect your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">2. Eligibility</h2>
              <p>
                You must be at least 13 years of age to use the Service. By using Cash Forth, you 
                represent and warrant that you meet this age requirement. If you are under 18, you 
                represent that you have your parent's or guardian's permission to use the Service.
              </p>
              <p>
                The Service is intended for personal, non-commercial use only. If you are using the 
                Service on behalf of a business or other entity, you represent that you have the 
                authority to bind that entity to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">3. Account Registration</h2>
              <p>
                To access certain features of the Service, you must create an account. Account 
                registration and authentication are provided through Clerk, a third-party 
                authentication service. You may register using:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Google authentication</li>
                <li>Email and password</li>
              </ul>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials 
                and for all activities that occur under your account. You agree to notify us 
                immediately of any unauthorized use of your account.
              </p>
              <p>
                You may also use the Service in "Guest Mode" without creating an account. Guest Mode 
                data is stored locally in your browser and is not backed up to our servers. Guest Mode 
                is free, has no time limit, and functions as an unlimited free trial — the only 
                trade-off is that your data is not saved across devices and will be lost if you clear 
                your browser storage.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">4. Subscriptions, Billing & Cancellation</h2>
              <p>
                Some features of the Service — including saving your data to our servers so it persists 
                across devices, workspace collaboration, and multi-device sync — require an active paid 
                subscription ("Subscription"). Guest Mode and read-only access to the Service remain 
                available without a Subscription.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.1 Pricing</h3>
              <p>Current Subscription plans and prices are:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Monthly:</strong> $5.00 USD per month, billed monthly</li>
                <li><strong>Annual:</strong> $50.00 USD per year, billed annually (equivalent to $4.17/month)</li>
              </ul>
              <p>
                All prices are in U.S. Dollars. Applicable taxes, if any, will be added at checkout and 
                shown on your invoice. The in-app currency display is a convenience feature for 
                forecasting your own transactions; it does not change the currency in which your 
                Subscription is billed.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.2 Payment Processor</h3>
              <p>
                All payments are processed by <strong>Stripe, Inc.</strong> ("Stripe"), our third-party 
                payment processor. When you subscribe, you are redirected to Stripe-hosted Checkout and 
                will be asked to provide payment information directly to Stripe. Cash Forth does not 
                receive, see, or store your full payment card number, CVC, or bank account number. We 
                only receive a Stripe customer identifier, a subscription identifier, and subscription 
                status metadata from Stripe. Your use of Stripe is governed by Stripe's{' '}
                <a href="https://stripe.com/legal/consumer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  terms
                </a>{' '}and{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  privacy policy
                </a>.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.3 Automatic Renewal</h3>
              <p>
                <strong>Subscriptions renew automatically.</strong> By subscribing, you authorize Cash 
                Forth and Stripe to charge your payment method on a recurring basis (monthly or 
                annually, matching your selected plan) until you cancel. Your Subscription will 
                continue to renew automatically at the then-current price for your plan until 
                terminated in accordance with these Terms.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.4 Cancellation</h3>
              <p>
                You may cancel your Subscription at any time through the <strong>Billing / Customer 
                Portal</strong>, which is accessible from your account menu inside Cash Forth. The 
                portal is operated by Stripe on our behalf. When you cancel:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your Subscription will remain active until the end of the current billing period that you have already paid for.</li>
                <li>You will not be charged again after cancellation.</li>
                <li>After the end of the current billing period, your account will revert to Guest Mode and server-side data saving will stop. Data previously saved to our servers may be retained as described in our Privacy Policy, but you should export any data you wish to keep before cancelling or, if access has already ended, contact us at contact@cashforth.com.</li>
              </ul>

              <h3 className="text-base font-medium mt-4 mb-2">4.5 Refunds</h3>
              <p>
                <strong>Subscription fees are non-refundable</strong>, except where a refund is 
                required by applicable law. We do not offer pro-rated refunds for partial billing 
                periods when you cancel. Because Guest Mode provides unlimited free access to the 
                core forecasting functionality, we encourage you to try the Service in Guest Mode 
                before subscribing.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.6 Failed Payments</h3>
              <p>
                If a renewal payment fails, Stripe will automatically retry the charge on our behalf 
                and may notify you by email. During this period, your Subscription status may move to 
                "past due" and server-side saving will be temporarily disabled until payment succeeds. 
                If payment is not successfully collected after Stripe's retry period, your 
                Subscription will be cancelled and your account will revert to Guest Mode.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.7 Price Changes</h3>
              <p>
                We may change Subscription prices from time to time. If we change the price for your 
                plan, we will give you at least <strong>30 days' advance notice by email</strong> 
                before the change takes effect. Existing subscribers will continue at their current 
                price until their next billing cycle after the notice period; after that, the new 
                price will apply on subsequent renewals. If you do not accept a price change, you may 
                cancel your Subscription through the Customer Portal before the new price takes effect.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.8 Free Trial</h3>
              <p>
                We do not currently offer a paid free trial. Instead, <strong>Guest Mode</strong> 
                functions as an unlimited free version of the Service: you can use the full 
                forecasting tool indefinitely without creating an account and without paying. Guest 
                Mode data is stored locally in your browser and is not saved to our servers.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">4.9 Taxes</h3>
              <p>
                You are responsible for any sales, use, VAT, GST, or other taxes associated with your 
                Subscription, except for taxes based on our net income. Where we are required by law to 
                collect taxes, they will be shown at checkout and on your invoice.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">5. Your Data & Content Ownership</h2>
              <p className="font-medium">
                This is an important section — please read it carefully.
              </p>
              <p>
                <strong>You own your data.</strong> Cash Forth claims no ownership rights over any 
                data, spreadsheets, transactions, or other content you enter into the Service 
                ("User Content"). All User Content remains your sole property.
              </p>
              <p>
                <strong>Limited license to provide the Service.</strong> By using the Service, you 
                grant Cash Forth a limited, non-exclusive license to store, process, and display 
                your User Content solely for the purpose of providing the Service to you. This 
                license ends when you delete your data or account.
              </p>
              <p>
                <strong>We do not use your data.</strong> We do not license, sell, share, or use 
                your User Content for any purpose other than providing the Service. We do not use 
                your financial data for analytics, advertising, or any other commercial purpose.
              </p>
              <p>
                <strong>Data storage.</strong> Your User Content is stored in our database (MongoDB) 
                to enable you to access it when logged in. We implement reasonable security measures 
                to protect your data, but we cannot guarantee absolute security.
              </p>
              <p>
                <strong>Data export and deletion.</strong> You may delete your account and associated 
                data at any time through your account settings or by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">6. Acceptable Use & Prohibited Conduct</h2>
              <p>
                You agree to use the Service only for lawful purposes and in accordance with these 
                Terms. You agree NOT to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Service for any illegal activities or in violation of any applicable laws</li>
                <li>Attempt to reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service</li>
                <li>Use automated scripts, bots, scrapers, or other automated means to access the Service</li>
                <li>Attempt to overload, disrupt, or interfere with the Service or its infrastructure</li>
                <li>Circumvent, disable, or otherwise interfere with security-related features of the Service</li>
                <li>Impersonate any person or entity, or falsely state or misrepresent your affiliation with a person or entity</li>
                <li>Access or attempt to access accounts or data belonging to other users</li>
                <li>Use the Service in any manner that could damage, disable, or impair the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">7. Intellectual Property</h2>
              <p>
                The Service, including its code, design, graphics, user interface, logos, and brand 
                identity (collectively, "Cash Forth IP"), is owned by Cash Forth and is protected by 
                copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                <strong>Limited license to use.</strong> Subject to your compliance with these Terms, 
                we grant you a limited, non-exclusive, non-transferable, revocable license to access 
                and use the Service for your personal, non-commercial purposes.
              </p>
              <p>
                This license does not include the right to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Modify, copy, or create derivative works based on the Service</li>
                <li>Use Cash Forth IP for any commercial purpose</li>
                <li>Remove any copyright or proprietary notices from the Service</li>
                <li>Transfer your license to any other person or entity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">8. Termination</h2>
              <p>
                <strong>Termination by you.</strong> You may stop using the Service and delete your 
                account at any time through your account settings or by contacting us at 
                contact@cashforth.com.
              </p>
              <p>
                <strong>Termination by us.</strong> We reserve the right to suspend or terminate 
                your access to the Service at any time, with or without cause, and with or without 
                notice. Reasons for termination may include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Violation of these Terms</li>
                <li>Conduct that we believe is harmful to other users, us, or third parties</li>
                <li>Discontinuation of the Service</li>
              </ul>
              <p>
                <strong>Effect of termination.</strong> Upon termination, your right to use the 
                Service will immediately cease. We may delete your account and User Content. 
                Sections of these Terms that by their nature should survive termination shall 
                survive, including but not limited to: ownership provisions, warranty disclaimers, 
                and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">9. Disclaimers & No Warranty</h2>
              <p className="font-medium uppercase text-sm">
                Please read this section carefully.
              </p>
              <p>
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY 
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, CASH FORTH DISCLAIMS ALL WARRANTIES, 
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
                <li>WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
                <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF ANY CALCULATIONS, PROJECTIONS, OR DATA</li>
                <li>WARRANTIES THAT DEFECTS WILL BE CORRECTED OR THAT THE SERVICE IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS</li>
              </ul>
              <p className="font-medium mt-4">
                Important notice regarding financial calculations:
              </p>
              <p>
                Cash Forth is a simple forecasting tool for personal use. The calculations, 
                projections, and data displayed by the Service may contain errors. YOU ARE SOLELY 
                RESPONSIBLE FOR VERIFYING THE ACCURACY OF ALL CALCULATIONS AND DATA. Do not rely 
                on the Service for important financial decisions without independent verification.
              </p>
              <p>
                We do not guarantee any particular level of uptime, availability, or data backup. 
                You are responsible for maintaining your own backups of any important data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">10. Limitation of Liability</h2>
              <p className="font-medium uppercase text-sm">
                Please read this section carefully.
              </p>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CASH FORTH, 
                ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                <li>LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES</li>
                <li>DAMAGES RESULTING FROM YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICE</li>
                <li>DAMAGES RESULTING FROM ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SERVERS OR ANY PERSONAL INFORMATION STORED THEREIN</li>
                <li>DAMAGES RESULTING FROM ANY ERRORS, MISTAKES, OR INACCURACIES IN THE SERVICE</li>
              </ul>
              <p className="mt-4">
                <strong>
                  OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THESE TERMS 
                  OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID 
                  US IN SUBSCRIPTION FEES DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING 
                  RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100 USD).
                </strong>
              </p>
              <p>
                Some jurisdictions do not allow the exclusion or limitation of certain damages. If 
                these laws apply to you, some or all of the above exclusions or limitations may not 
                apply, and you may have additional rights.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">11. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Cash Forth and its officers, 
                directors, employees, agents, and affiliates from and against any claims, 
                liabilities, damages, losses, costs, or expenses (including reasonable attorneys' 
                fees) arising out of or related to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your User Content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">12. Modifications to the Service</h2>
              <p>
                We reserve the right to modify, suspend, or discontinue the Service (or any part 
                thereof) at any time, with or without notice. We shall not be liable to you or any 
                third party for any modification, suspension, or discontinuation of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">13. General Provisions</h2>
              
              <h3 className="text-base font-medium mt-4 mb-2">Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the 
                United States, without regard to its conflict of law provisions.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">Changes to Terms</h3>
              <p>
                We may update these Terms from time to time. We will notify you of any material 
                changes by posting the new Terms on this page and updating the "Last Updated" date. 
                Your continued use of the Service after any changes constitutes your acceptance of 
                the new Terms.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">Severability</h3>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that 
                provision shall be limited or eliminated to the minimum extent necessary, and the 
                remaining provisions shall remain in full force and effect.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">Entire Agreement</h3>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement 
                between you and Cash Forth regarding your use of the Service and supersede all 
                prior agreements and understandings.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">Waiver</h3>
              <p>
                Our failure to enforce any right or provision of these Terms shall not be deemed 
                a waiver of such right or provision.
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">Assignment</h3>
              <p>
                You may not assign or transfer these Terms or your rights hereunder without our 
                prior written consent. We may assign these Terms without restriction.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">14. Contact Us</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Cash Forth</strong><br />
                Email: contact@cashforth.com
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
